import { toast } from 'react-toastify';
import { useContext, useEffect, useState } from 'react';
import { RootState } from '../../Redux/Store';
import { AppContext } from '../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import { updatePreferences } from '../../Redux/Reducers/PreferencesSlice';
import { isEntitled } from '../../Utils/ContentOrConfigUtil';
import { isEmptyObject } from '../../Utils/Basic';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { EVENT, LEAD, SCREEN } from '../../Constants/AnalyticsConstants';
import { TPreferencesProps } from './Preferences.type';
import PreferenceComponent from '../../Components/PreferenceComponent/PreferenceComponent';
import './Preferences.scss';
import { MinMaxErrorMsg } from '../../Constants/Constants';

const Preferences = (props: TPreferencesProps) => {
  const dispatch = useAppDispatch();
  const {
    showBanner,
    configTableData: {
      FPO_APP_CONFIG: { APP_INIT, PREFERENCES_INIT } = { PREFERENCES_INIT: [], APP_INIT: {} },
    } = {},
  } = useAppSelector((state: RootState) => state.ContentOrConfig) || {};
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { preferencesData } = useAppSelector((state: RootState) => state.Preferences);
  const [expandedAccord, setExpandedAccord] = useState('');
  const [userInteractedPayload, setUserInteractedPayload] = useState<any>({});
  const { isMobile, preferencesClicked } = useContext(AppContext);
  const [maxMilesError, setMaxMilesError] = useState<any>('');
  const preferredminmilesValue =
    userInteractedPayload?.preferredminmiles ||
    preferencesData?.find((item: any) => item?.key === 'preferredminmiles')?.value ||
    '';
  const preferredmaxmiles =
    userInteractedPayload?.preferredmaxmiles ||
    preferencesData?.find((item: any) => item?.key === 'preferredmaxmiles')?.value ||
    '';
  useEffect(() => {
    if (preferredmaxmiles > preferredminmilesValue) {
      setMaxMilesError('');
    } else {
      if (expandedAccord !== 'advancedSearch') setExpandedAccord('advancedSearch');
      setMaxMilesError(MinMaxErrorMsg);
    }
  }, [preferredmaxmiles, preferredminmilesValue]);
  const handleAccordClick = (key: string) => setExpandedAccord(expandedAccord === key ? '' : key);
  const handleChange = (type: string, slug: string, value: string, defaultValue: string = '') => {
    setUserInteractedPayload((prevState: any) => {
      switch (type) {
        case 'radio':
          return { ...prevState, [slug]: value };
        case 'dropdown':
          return { ...prevState, [slug]: value };
        case 'checkbox': {
          let updatedValue;
          if (value === 'none') {
            updatedValue = value;
          } else {
            const currentValues = (prevState[slug] || defaultValue)?.split(',') || [];
            if (currentValues.includes(value)) {
              updatedValue =
                currentValues.filter((item: string) => item !== value).join(',') || 'none';
            } else {
              updatedValue = [
                ...currentValues.filter((item: string) => item !== 'none' && !!item),
                value,
              ].join(',');
            }
          }
          return { ...prevState, [slug]: updatedValue };
        }
        default:
          return prevState;
      }
    });
  };
  const handleSave = () => {
    if (maxMilesError) return;
    const userAlteredAccordTitle = PREFERENCES_INIT?.reduce((acc: string[], accord: any) => {
      accord.ACCORD_CONTENT.forEach((content: any) => {
        if (userInteractedPayload.hasOwnProperty(content.API_KEY)) {
          acc.push(accord.ACCORD_TITLE.toLowerCase().replace(/\s+/g, '_'));
        }
      });
      return acc;
    }, []).join(',');
    fbLogEvent(EVENT.UE_PREFERENCES_SAVE, {
      screen: SCREEN.PREFERENCES,
      lead: isMobile
        ? SCREEN.MANAGE_MENU
        : preferencesClicked
          ? SCREEN.TOP_MENU_WEB
          : LEAD.LEFT_NAV,
      preference_item: userAlteredAccordTitle,
    });
    const payload = { ...userInteractedPayload };
    if (payload.preferredLoadExclusion && payload.preferredLoadExclusion === 'none') {
      payload.preferredLoadExclusion = '';
    }
    const callback = (data: any) => {
      const { message, status } = data;
      toast(message);
      if (status === 'SUCCESS') {
        fbLogEvent(EVENT.UE_PREFERENCES_SAVE_SUCCESS, {
          screen: SCREEN.PREFERENCES,
          lead: isMobile
            ? SCREEN.MANAGE_MENU
            : preferencesClicked
              ? SCREEN.TOP_MENU_WEB
              : LEAD.LEFT_NAV,
          preference_item: userAlteredAccordTitle,
        });
        setExpandedAccord('');
        setUserInteractedPayload({});
      } else {
        fbLogEvent(EVENT.UE_PREFERENCES_SAVE_FAIL, {
          screen: SCREEN.PREFERENCES,
          lead: isMobile
            ? SCREEN.MANAGE_MENU
            : preferencesClicked
              ? SCREEN.TOP_MENU_WEB
              : LEAD.LEFT_NAV,
          preference_item: userAlteredAccordTitle,
        });
      }
    };
    dispatch(
      updatePreferences({
        payload: {
          preferences: Object.entries(payload)?.map(([_key, _value]) => {
            return { key: _key, value: _value };
          }),
        },
        callback,
      }),
    );
  };

  return (
    <div className={`Preferences ${showBanner ? ' AlertBannerVisible' : ''}`}>
      <div className="Preferences_Header">Preferences</div>
      {PREFERENCES_INIT?.filter((item: any) =>
        isEntitled(memberData?.member, APP_INIT, 'preferences', item?.ACCORD_ROLE_FILTER_KEY),
      )?.map((accord: any) => {
        const userAlteredData = accord?.ACCORD_CONTENT?.reduce((acc: any, item: any) => {
          if (userInteractedPayload[item?.API_KEY]) {
            acc[item?.API_KEY] = userInteractedPayload[item?.API_KEY];
          }
          return acc;
        }, {});

        const storedPreferences = preferencesData?.reduce((acc: any, pref: any) => {
          const item = accord?.ACCORD_CONTENT?.find(
            (contentItem: any) => contentItem?.API_KEY === pref?.key,
          );
          if (item) {
            acc[pref?.key] = pref?.value;
          }
          return acc;
        }, {});

        return (
          <PreferenceComponent
            slug={accord?.ACCORD_ROLE_FILTER_KEY}
            label={accord?.ACCORD_TITLE}
            content={accord?.ACCORD_CONTENT}
            isExpanded={accord?.ACCORD_ROLE_FILTER_KEY === expandedAccord}
            handleAccordClick={handleAccordClick}
            storedPreferences={storedPreferences}
            userAlteredData={userAlteredData}
            handleChange={handleChange}
            key={accord?.ACCORD_TITLE}
            maxMilesError={maxMilesError}
          />
        );
      })}
      {!!PREFERENCES_INIT?.filter((item: any) =>
        isEntitled(memberData?.member, APP_INIT, 'preferences', item?.ACCORD_ROLE_FILTER_KEY),
      )?.length && (
        <button
          className={`Save_Button primary-cta-btn`}
          disabled={isEmptyObject(userInteractedPayload) || !!maxMilesError}
          onClick={handleSave}
        >
          Save
        </button>
      )}
    </div>
  );
};

export default Preferences;
